import { labels } from "../ui/labels.js";
import { markers } from "../ui/screenMarker.js";
import { models } from "../models/models.js";
import { slides } from "../slides/slides.js";
import { clipping } from "../tools/clipping.js";
import { world } from "../world/world.js";

var controller = { public: {} };

var allSceneStates = []; // Flat list of SceneStates like in the scene.json file
var sceneStateByID = {}; // SceneStates indexed to the ID of the SceneState for easy access
var currentActiveSceneState = null; // Track the currently active scene state - INTERNAL to controller

class SceneState {
  constructor(options) {
    this.name = options.name;
    this.id = options.id;
    this.models = [];
    this.labels = [];
    this.slides = [];
    this.clippingPlanes = [];
    this.world = null;
    this.labelsToggleState = true; // Track label visibility toggle state for this scene
    this.markersToggleState = true; // Track marker visibility toggle state for this scene
  }

  addSlides(idArray) {
    idArray.map((id) => {
      id = parseInt(id);
      if (isNaN(id)) {
        console.log("SlideID should be a number.");
      } else {
        this.slides.push(id);
      }
    });
  }

  addModels(idArray) {
    idArray.map((id) => {
      id = parseInt(id);
      if (isNaN(id)) {
        console.log("ModelID should be a number.");
      } else {
        this.models.push(id);
      }
    });
  }

  addLabels(idArray) {
    idArray.map((id) => {
      id = parseInt(id);
      if (isNaN(id)) {
        console.log("LabelID should be a number");
        console.log(id);
      } else {
        this.labels.push(id);
      }
    });
  }

  addClippingPlanes(idArray) {
    idArray.map((id) => {
      id = parseInt(id);
      if (isNaN(id)) {
        console.log("clippingPlaneID should be a number");
      } else {
        this.clippingPlanes.push(id);
      }
    });
  }

  setWorldState(worldState) {
    this.world = JSON.parse(JSON.stringify(worldState));
    // Treat null grid visibility as unset; scene-state activation resolves unset
    // visibility to the project's initial loaded grid visibility/default.
    if (this.world && this.world.grid && this.world.grid.visible === null) {
      delete this.world.grid.visible;
    }
  }
}

controller.create = function createSceneState(options) {
  if (options === undefined) {
    options = {};
  }

  if (!options.name) {
    options.name = "Untitled Scene State";
  }
  if (!Object.prototype.hasOwnProperty.call(options, "id")) {
    options.id = findID();
  }

  const tempSceneState = new SceneState(options);
  sceneStateByID[options.id] = tempSceneState;
  allSceneStates.push(tempSceneState);
  return tempSceneState;
};

controller.clone = function createSceneState(targetID) {
  const toClone = sceneStateByID[targetID];
  if (!toClone) {
    console.log("Unable to clone SceneState. Invalid ID");
    return;
  }

  const id = findID();

  const tempSceneState = new SceneState({ name: toClone.name + " (copy)" });
  tempSceneState.id = id;
  sceneStateByID[id] = tempSceneState;
  allSceneStates.push(tempSceneState);

  tempSceneState.addModels(toClone.models.slice(0));
  tempSceneState.addSlides(toClone.slides.slice(0));
  tempSceneState.addLabels(toClone.labels.slice(0));
  tempSceneState.addClippingPlanes(toClone.clippingPlanes.slice(0));

  if (toClone.world) {
    tempSceneState.setWorldState(JSON.parse(JSON.stringify(toClone.world)));
  }
  
  // Copy label toggle state
  tempSceneState.labelsToggleState = toClone.labelsToggleState;

  return id;
};

controller.public.getContainingModel = function getContainingModel(modelID) {
  const idList = [];
  allSceneStates.map((ss) => {
    if (ss.models.includes(modelID)) {
      idList.push(ss.id);
    }
  });
  return idList;
};

controller.public.addModelToList = function addModelToList(modelID, list) {
  // Remove model from all scene states first
  allSceneStates.map((state) => {
    if (!state) return;
    if (state.models.includes(modelID)) {
      if (!list.includes(state.id)) {
        state.models.splice(state.models.indexOf(modelID), 1);
      }
    } else if (list.includes(state.id)) {
      state.models.push(modelID);
    }
  });
};

controller.createFromCurrentState = function createFromCurrentState() {
  const tempState = controller.create();
  const modelsVisible = models.getVisible();
  const slidesVisible = slides.getVisible();
  const labelsVisible = labels.getVisible();
  const clippingPlanesActive = clipping.getActive();
  const worldState = world.serialize({ forSceneState: true });

  tempState.addModels(modelsVisible);
  tempState.addSlides(slidesVisible);
  tempState.addLabels(labelsVisible);
  tempState.addClippingPlanes(clippingPlanesActive);
  tempState.setWorldState(worldState);

  return tempState.id;
};

controller.idIsValid = function idIsValid(id) {
  if (sceneStateByID[id] === undefined) {
    return false;
  }
  return true;
};

function findID() {
  // Find the largest ID number in use and return 1 greater.
  let idArray = Object.keys(sceneStateByID)
    .filter((key) => !isNaN(parseInt(key)))
    .map((key) => parseInt(key))
    .sort((a, b) => a - b);
  if (idArray.length == 0) {
    return 1;
  }

  let id = idArray[idArray.length - 1] + 1;
  return id;
}

controller.public.activateSceneState = function activateSceneState(sceneStateID, options) {
  const tempSceneState = sceneStateByID[sceneStateID];
  if (tempSceneState === undefined) {
    console.log("Invalid Scenestate");
    return;
  }

  controller.public.setCurrentActive(tempSceneState);

  let excludeModels = false;
  let exludeLabels = false;
  let excludeSlides = false;
  let excludeWorld = false;
  const excludeClippingPlanes = false;

  if (options !== undefined) {
    if (options.excludeModels) {
      excludeModels = options.excludeModels;
    }

    if (options.exludeLabels) {
      exludeLabels = options.excludeLabels;
    }

    if (options.excludeSlides) {
      excludeSlides = options.excludeSlides;
    }

    if (options.excludeWorld) {
      excludeWorld = options.excludeWorld;
    }
  }

  if (!excludeModels) {
    models.hideAll();
    models.batchSetVisible(tempSceneState.models, true);
  }

  if (!exludeLabels) {
    labels.hideAll();
    // Set visibility once based on toggle state to avoid double updates
    const shouldShow = tempSceneState.labelsToggleState !== false;
    labels.batchSetVisible(tempSceneState.labels, shouldShow);
  }

  // Apply markers toggle state for this scene
  if (tempSceneState.markersToggleState !== undefined) {
    const shouldShowMarkers = tempSceneState.markersToggleState !== false;
    const currentlyVisible = markers.public.getVisible();
    // Only toggle if the state doesn't match
    if (shouldShowMarkers !== currentlyVisible) {
      markers.public.toggleAll();
    }
  }

  if (!excludeSlides) {
    slides.hideAll();
    slides.batchSetVisible(tempSceneState.slides, true);
  }

  if (!excludeClippingPlanes) {
    clipping.deactivateAll();
    clipping.batchActivate(tempSceneState.clippingPlanes);
  }

  if (!excludeWorld) {
    if (tempSceneState.world) {
      world.deserialize(tempSceneState.world, { fromSceneState: true });
    } else {
      world.public.forceGridToDefault();
    }
  }

};

// Add getter for current active scene state - PUBLIC method
controller.public.getCurrentActive = function getCurrentActive() {
  return currentActiveSceneState;
};

// Add getter for scene state by ID - PUBLIC method
controller.public.getSceneStateByID = function getSceneStateByID(id) {
  return sceneStateByID[id];
};

// Add getter for all scene states - PUBLIC method
controller.public.getAllSceneStates = function getAllSceneStates() {
  return allSceneStates;
};

controller.public.captureCurrentWorldState = function captureCurrentWorldState() {
  if (!currentActiveSceneState) {
    return null;
  }

  const worldState = world.serialize({ forSceneState: true });
  currentActiveSceneState.setWorldState(worldState);

  return currentActiveSceneState.world;
};

// Check if any scene state has labels or markers enabled - PUBLIC method
controller.public.areAnyLabelsOrMarkersEnabled = function areAnyLabelsOrMarkersEnabled() {
  if (allSceneStates.length === 0) {
    return { labels: false, markers: false };
  }
  
  const anyLabelsEnabled = allSceneStates.some(sceneState => sceneState.labelsToggleState === true);
  const anyMarkersEnabled = allSceneStates.some(sceneState => sceneState.markersToggleState === true);
  
  return {
    labels: anyLabelsEnabled,
    markers: anyMarkersEnabled
  };
};

// Add setter for current active scene state - PUBLIC method
controller.public.setCurrentActive = function setCurrentActive(sceneState) {
  currentActiveSceneState = sceneState;

  // Notify UI components that scene state changed
  if (window.refreshWorldUI) {
    window.refreshWorldUI();
  }
};

// Initialize scene states system - PUBLIC method
controller.public.initialize = function initialize() {
  controller.public.setCurrentActive(null);
};

// Finds Unused SceneStates that can be deleted.
function removeOrphaned() {
  // This deletes scene states that exist but are not used by anything.
  // HACK
  // FIXME THIS SHOULD NOT USE THE GLOBAL WINDOW.INVENTUM. IMPORTING views causes a failure elsewhere. Possibly a cyclic dependency or something.
  const animationScenestates = window.Inventum.animations.getScenestates();
  const viewScenestates = window.Inventum.camera.getScenestates();

  allSceneStates = allSceneStates.filter((sceneState) => {
    let isUsed = false;
    const id = sceneState.id;

    if (animationScenestates[id] || viewScenestates[id]) {
      isUsed = true;
    }

    if (!isUsed) {
      delete sceneStateByID[id];
      // If we're removing the active scene state, clear it properly
      if (currentActiveSceneState && currentActiveSceneState.id === id) {
        controller.public.setCurrentActive(null);
      }
    }

    return isUsed;
  });
}

function removeMissingRefs() {
  // Deletes references from scenestates that no longer exist.
  // i.e if a model was number 7 and then was deleted this will deletes the references
  const modelsIDS = window.Inventum.models.getModelsIDS();
  const labelsIDS = window.Inventum.labels.getLabelIDS();
  const slidesIDS = window.Inventum.slides.getSlidesIDS();
  const clippingPlanesIDS = window.Inventum.clipping.getClippingPlanesIDS();

  allSceneStates.map((sceneState) => {
    sceneState.models = sceneState.models.filter((modelID) => {
      return modelsIDS.includes(modelID.toString());
    });
    sceneState.labels = sceneState.labels.filter((labelID) => {
      return labelsIDS.includes(labelID.toString());
    });
    sceneState.slides = sceneState.slides.filter((slideID) => {
      return slidesIDS.includes(slideID.toString());
    });
    sceneState.clippingPlanes = sceneState.clippingPlanes.filter((clippingPlaneID) => {
      return clippingPlanesIDS.includes(clippingPlaneID.toString());
    });
  });
}

controller.public.clean = function clean() {
  // Runs the cleanup functions. Modifys the variables allSceneStates and sceneStateByID;
  removeOrphaned();
  removeMissingRefs();
};

// Returns all the SceneStates being used in the scene in the format of the scene.json file
controller.generateJSON = function generateJSON() {
  controller.public.clean();
  return { scenestates: allSceneStates };
};

controller.reset = function reset() {
  allSceneStates = [];
  sceneStateByID = {};
  controller.public.setCurrentActive(null);
};

export { controller as sceneStates };
